<template>
  <div class="form__field" :class="`--${size}`">
    <label :for="name" class="label">
      {{ label }}
      <span v-if="required" class="required-symbol">*</span>
    </label>
    <textarea ref="editor" class="wysiwyg" :id="name" :name="name" @focusout="input" :value="modelValue.value"
              :placeholder="placeholder"/>
    <p v-if="message" class="note">
      {{ $t(`form.messages.${name}`) }}
    </p>
    <p :class="['input-error', { '--hidden': !(!modelValue.valid && onFocus) }]">
      {{ $t(`form.errors.${name}`) }}
    </p>
  </div>
</template>

<script>
import {REGEX} from '@/helpers/regex.js'
import {image_uploader, video_uploader} from '@/plugins/TinyMCE/media_uploader.js'
import {file_picker_callback} from '@/plugins/TinyMCE/file_picker_callback.js'
import '@/plugins/TinyMCE/youtube_autoembed.js'

export default {
  name: 'FormWysiwyg',
  props: {
    label: {
      type: String,
      required: true
    },
    message: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: Object,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: null
    },
    required: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      onFocus: false
    }
  },
  methods: {
    validate(value) {
      if (this.required === false && value === '') {
        return true
      }
      return REGEX.WYSIWYG.test(value)
    }
  },
  emits: ['update:modelValue'],
  mounted() {
    this.$emit('update:modelValue', {
      value: this.modelValue.value,
      valid: this.validate(this.modelValue.value)
    })

    window.tinymce.init({
      target: this.$refs.editor,
      menubar: false,
      plugins: ['emoticons', 'fullscreen', 'image', 'link', 'lists', 'media', 'youtube_autoembed'],
      block_formats: 'Paragraph=p; Heading 3=h3; Heading 4=h4; Heading 5=h5;',
      content_style: '.mce-content-body img {width: max-content; max-width: 70%; height: auto; object-fit: cover; overflow: hidden;} .mce-content-body video, .mce-content-body iframe {width: 100%; height: 100%; object-fit: cover;} .mce-preview-object {width: 50%; height: auto; aspect-ratio: 16/9; border: none;}',
      style_formats: [
        {title: 'Heading 3', block: 'h3'},
        {title: 'Heading 4', block: 'h4'},
        {title: 'Heading 5', block: 'h5'},
        {title: 'Paragraph', block: 'p'}
      ],
      toolbar: 'blocks bold italic | bullist numlist | image media | link superscript | forecolor backcolor emoticons | fullscreen',
      valid_elements: '*[*]',
      extended_valid_elements: 'iframe[src|title|width|height|allow|allowfullscreen|frameborder|class]',
      valid_children: '+div[iframe]',
      image_description: false,
      image_dimensions: false,
      images_upload_handler: image_uploader,
      file_picker_callback: file_picker_callback,
      file_picker_types: 'media',
      media_alt_source: false,
      media_dimensions: false,
      media_poster: false,
      object_resizing: false,
      media_upload_handler: video_uploader,
      setup: (editor) => {
        const style = document.createElement('style')
        style.textContent = `
          /* Match dialogs by title text */
          .tox-dialog:has(.tox-dropzone) .tox-dialog__body-content::before {
            content: "Images: max 2 MB";
            display: block;
            margin: 6px 0 10px;
            padding: 8px;
            border: 1px solid #ddd;
            border-radius: 6px;
            font-size: 12px;
            line-height: 1.3;
            background: #fafafa;
          }

          .tox-dialog:has(.tox-icon.tox-tbtn__icon-wrap) .tox-dialog__body-content::before {
            content: "Videos: max 15 MB";
            display: block;
            margin: 6px 0 10px;
            padding: 8px;
            border: 1px solid #ddd;
            border-radius: 6px;
            font-size: 12px;
            line-height: 1.3;
            background: #fafafa;
          }
        `
        document.head.appendChild(style)

        editor.on('change', () => {
          this.onFocus = true

          const content = editor.getContent()
          this.$emit('update:modelValue', {
            value: content,
            valid: this.validate(content)
          })
        })

        editor.on('input', () => {
          this.onFocus = true

          const content = editor.getContent()
          this.$emit('update:modelValue', {
            value: content,
            valid: this.validate(content)
          })
        })
      }
    })
  },
  beforeUnmount() {
    if (this.$refs.editor && window.tinymce) {
      const editor = window.tinymce.get(this.$refs.editor.id)
      if (editor) {
        editor.remove()
      }
    }
  }
}
</script>